/**
 * @file toolbar-divider.tsx
 *
 * A decorative divider for use inside MD3 Toolbar components.
 * Renders as a thin line that visually separates groups of toolbar actions.
 *
 * @see https://m3.material.io/components/toolbars/guidelines (Anatomy → Flexibility & slots)
 */

import type * as React from "react";
import { cn } from "../../lib/utils";
import { useToolbarContext } from "./toolbar-context";
import { FloatingToolbarTokens, ToolbarDividerTokens } from "./toolbar-tokens";

export interface ToolbarDividerProps {
	/**
	 * The toolbar's orientation context.
	 * - `"horizontal"` toolbar → renders a **vertical** divider line.
	 * - `"vertical"` toolbar → renders a **horizontal** divider line.
	 * @default "horizontal"
	 */
	orientation?: "horizontal" | "vertical";
	className?: string;
}

/**
 * A thin decorative separator for grouping actions inside a Toolbar.
 *
 * Inherits its color from the toolbar's `--toolbar-color` CSS variable via
 * `currentColor`, so it automatically adapts to both standard and vibrant
 * color configurations.
 *
 * @example
 * ```tsx
 * <HorizontalFloatingToolbar expanded={true}>
 *   <IconButton aria-label="Bold"><BoldIcon /></IconButton>
 *   <IconButton aria-label="Italic"><ItalicIcon /></IconButton>
 *   <ToolbarDivider />
 *   <IconButton aria-label="Align left"><AlignLeftIcon /></IconButton>
 * </HorizontalFloatingToolbar>
 * ```
 */
export const ToolbarDivider: React.FC<ToolbarDividerProps> = ({
	orientation: propsOrientation,
	className,
}) => {
	const context = useToolbarContext();
	const orientation = propsOrientation || context?.orientation || "horizontal";
	const isHorizontalToolbar = orientation === "horizontal";

	return (
		<hr
			aria-orientation={isHorizontalToolbar ? "vertical" : "horizontal"}
			className={cn(
				"shrink-0 bg-current opacity-30 border-none",
				isHorizontalToolbar
					? "w-px h-8 self-center mx-1"
					: "h-px w-8 self-center my-1",
				className,
			)}
			style={{
				width: isHorizontalToolbar
					? `${ToolbarDividerTokens.Thickness}px`
					: `${ToolbarDividerTokens.HeightRatio * FloatingToolbarTokens.ContainerHeight}px`,
				height: isHorizontalToolbar
					? `${ToolbarDividerTokens.HeightRatio * FloatingToolbarTokens.ContainerHeight}px`
					: `${ToolbarDividerTokens.Thickness}px`,
			}}
		/>
	);
};

ToolbarDivider.displayName = "ToolbarDivider";
